// pages/TaskManagement/EndOfDay.js
//
// Legacy: TasksEndOfDayReview — the end-of-shift grid where an employee
// reviews and corrects TODAY's time-log entries before leaving.
// Round TM-9 (2026) — audit Deliverable 9:
//   • the grid edits start/end/description; delete removes a row;
//     "Add entry" inserts a row for one of your open tasks;
//   • Save is the legacy full-replace (delete today's rows + re-insert);
//     real times are left NULL server-side (legacy behavior — totals
//     everywhere use user-stated times since Round TM-4);
//   • the legacy validations run SERVER-side and come back as a list;
//   • Print replaces the legacy Crystal print.
// Backend: GET/POST /api/TaskManagement/eod-review

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Card, CardBody, Badge, Alert, Button, Table, Input, Label } from "reactstrap";
import { DashboardPageHeader, EmptyState, TableSkeleton } from "../../Components/Common/DashboardKit";
import api from "../../api";

const toTimeInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const timeToIso = (hhmm) => {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(":").map(Number);
    const d = new Date();
    d.setHours(h || 0, m || 0, 0, 0);
    return d.toISOString();
};
const rowMinutes = (r) => {
    if (!r.start || !r.end) return 0;
    const [sh, sm] = r.start.split(":").map(Number);
    const [eh, em] = r.end.split(":").map(Number);
    return Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
};

const EndOfDay = () => {
    document.title = "End of Day Review || SSW Technologies Dashboard";

    const [rows, setRows] = useState([]);
    const [available, setAvailable] = useState([]);
    const [addTaskId, setAddTaskId] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState([]);
    const [error, setError] = useState("");
    const [savedMsg, setSavedMsg] = useState("");

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            setErrors([]);
            const res = await api.get("/TaskManagement/eod-review");
            const data = res?.data ?? res;
            setRows((data?.items || []).map((r) => ({
                taskId: r.taskId,
                description: r.description,
                start: toTimeInput(r.start),
                end: toTimeInput(r.end),
                descriptionOfWork: r.descriptionOfWork || "",
                invalid: !!r.invalid,
            })));
            setAvailable(data?.availableTasks || []);
        } catch (err) {
            setError(err?.response?.data?.message || "Unable to load End of Day review.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const totalMinutes = useMemo(
        () => rows.reduce((sum, r) => sum + rowMinutes(r), 0),
        [rows]
    );

    const patchRow = (i, field, value) =>
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));

    const removeRow = (i) =>
        setRows((prev) => prev.filter((_, idx) => idx !== i));

    const addRow = () => {
        const id = parseInt(addTaskId, 10);
        if (Number.isNaN(id)) return;
        const t = available.find((a) => a.taskId === id);
        setRows((prev) => [...prev, {
            taskId: id,
            description: t?.description || `Task #${id}`,
            start: "",
            end: "",
            descriptionOfWork: "",
            invalid: false,
        }]);
        setAddTaskId("");
    };

    const save = async () => {
        try {
            setSaving(true);
            setError("");
            setErrors([]);
            setSavedMsg("");
            await api.post("/TaskManagement/eod-review", {
                rows: rows.map((r) => ({
                    taskId: r.taskId,
                    start: timeToIso(r.start),
                    end: timeToIso(r.end),
                    descriptionOfWork: r.descriptionOfWork || null,
                })),
            });
            setSavedMsg("End of Day review saved.");
            await load();
        } catch (err) {
            const data = err?.response?.data;
            if (Array.isArray(data?.errors) && data.errors.length > 0) {
                setErrors(data.errors);
            } else {
                setError(data?.message || "Unable to save End of Day review.");
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-time-line"
                    title="End of Day Review"
                    subtitle="Review and correct today's time-log entries before you leave"
                    tone="default"
                    breadcrumb={[
                        { label: "Task Management", to: "/task-management" },
                        { label: "End of Day" },
                    ]}
                />

                {error && <Alert color="danger">{error}</Alert>}
                {savedMsg && <Alert color="success">{savedMsg}</Alert>}
                {errors.length > 0 && (
                    <Alert color="danger">
                        <div className="fw-semibold mb-1">Please correct the following before saving:</div>
                        <ul className="mb-0">
                            {errors.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                    </Alert>
                )}

                <Card className="shadow-sm border-0">
                    <CardBody>
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 d-print-none">
                            <div className="d-flex align-items-center gap-2">
                                <Label className="mb-0">Add entry:</Label>
                                <Input
                                    type="select"
                                    bsSize="sm"
                                    style={{ width: "340px" }}
                                    value={addTaskId}
                                    onChange={(e) => setAddTaskId(e.target.value)}
                                >
                                    <option value="">— pick one of your open tasks —</option>
                                    {available.map((t) => (
                                        <option key={t.taskId} value={t.taskId}>
                                            #{t.taskId} — {t.description}
                                        </option>
                                    ))}
                                </Input>
                                <Button size="sm" color="light" onClick={addRow} disabled={!addTaskId}>
                                    <i className="ri-add-line align-bottom"></i> Add
                                </Button>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <Badge color="info" className="fs-6">
                                    Total: {Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m
                                </Badge>
                                <Button size="sm" color="light" onClick={() => window.print()}>
                                    <i className="ri-printer-line align-bottom me-1"></i>Print
                                </Button>
                                <Button size="sm" color="primary" onClick={save} disabled={saving || loading}>
                                    {saving ? "Saving..." : "Save Review"}
                                </Button>
                            </div>
                        </div>

                        {loading ? (
                            <TableSkeleton rows={5} cols={6} />
                        ) : rows.length === 0 ? (
                            <EmptyState
                                icon="ri-time-line"
                                title="No entries today"
                                message="Add an entry above if you worked on a task without logging it."
                            />
                        ) : (
                            <div className="table-responsive">
                                <Table size="sm" className="align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Task</th>
                                            <th style={{ width: "130px" }}>Start</th>
                                            <th style={{ width: "130px" }}>End</th>
                                            <th>Description of Work</th>
                                            <th className="text-end" style={{ width: "90px" }}>Minutes</th>
                                            <th className="d-print-none" style={{ width: "60px" }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r, i) => (
                                            <tr key={i} className={r.invalid ? "table-danger" : ""}>
                                                <td className="text-truncate" style={{ maxWidth: "300px" }}>
                                                    <span className="fw-semibold">#{r.taskId}</span>{" "}
                                                    <span className="text-muted">{r.description}</span>
                                                </td>
                                                <td>
                                                    <Input
                                                        type="time"
                                                        bsSize="sm"
                                                        value={r.start}
                                                        onChange={(e) => patchRow(i, "start", e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <Input
                                                        type="time"
                                                        bsSize="sm"
                                                        value={r.end}
                                                        onChange={(e) => patchRow(i, "end", e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <Input
                                                        type="text"
                                                        bsSize="sm"
                                                        placeholder="Required for entries of 2 hours or more"
                                                        value={r.descriptionOfWork}
                                                        onChange={(e) => patchRow(i, "descriptionOfWork", e.target.value)}
                                                    />
                                                </td>
                                                <td className="text-end">{rowMinutes(r)}</td>
                                                <td className="d-print-none text-end">
                                                    <Button
                                                        size="sm"
                                                        color="link"
                                                        className="text-danger p-0"
                                                        onClick={() => removeRow(i)}
                                                        title="Remove this entry"
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default EndOfDay;
